const MS_PER_DAY = 24 * 60 * 60 * 1000

export interface DaysLeft {
  label: string
  urgent: boolean
}

/**
 * Remaining days until `expiresAt`, rounded up.
 * 10 hours left → 1. Already past → 0. Invalid timestamp → 0.
 */
export function daysLeftFromExpiry(
  expiresAt: string,
  now = Date.now(),
): number {
  const expires = Date.parse(expiresAt)
  if (Number.isNaN(expires)) return 0
  const remaining = expires - now
  if (remaining <= 0) return 0
  return Math.ceil(remaining / MS_PER_DAY)
}

/** Labels from a remaining-days integer (ceil'd). */
export function formatDaysLeft(days: number): DaysLeft {
  if (days <= 0) return { label: 'Expired', urgent: true }
  if (days <= 1) return { label: 'Ends today', urgent: true }
  return { label: `${days} days left`, urgent: false }
}

/** Labels from `expiresAt` — enough to refresh; does not tick live. */
export function formatExpiry(expiresAt: string, now = Date.now()): DaysLeft {
  return formatDaysLeft(daysLeftFromExpiry(expiresAt, now))
}

/** ISO timestamp `days` in the future — for sample data that shouldn't age. */
export function daysFromNow(days: number): string {
  return new Date(Date.now() + days * MS_PER_DAY).toISOString()
}

/** Value for `<input type="datetime-local">` in the user's timezone. */
export function toDatetimeLocal(date: Date): string {
  const offsetMs = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16)
}

const MS_PER_MINUTE = 60_000
const MS_PER_HOUR = 60 * MS_PER_MINUTE

/**
 * Remaining wait until `iso`, rounded up.
 * "in 40 minutes" · "in 3 hours" · "now" if already past.
 */
export function formatWaitUntil(iso: string, now = Date.now()): string {
  const at = Date.parse(iso)
  if (Number.isNaN(at)) return ''
  const remaining = at - now
  if (remaining <= 0) return 'now'
  const minutes = Math.ceil(remaining / MS_PER_MINUTE)
  if (minutes < 60) {
    return minutes === 1 ? 'in 1 minute' : `in ${minutes} minutes`
  }
  const hours = Math.ceil(remaining / MS_PER_HOUR)
  if (hours < 24) {
    return hours === 1 ? 'in 1 hour' : `in ${hours} hours`
  }
  const days = Math.ceil(remaining / MS_PER_DAY)
  return days === 1 ? 'in 1 day' : `in ${days} days`
}
